import React, {ReactNode, useMemo, useState} from "react";
import IconButton from "../button/icon_button";
import {Add, ArrowForwardIos, Close} from "@mui/icons-material";
import {makeStyles} from "@mui/styles";
import {CircularProgress, IconButtonProps} from "@mui/material";
import classnames from "classnames";


const useStyles = makeStyles((theme) => ({
    border: {
        // @ts-ignore
        backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23${theme.palette.text.disabled.slice(1)}' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
    }
}))

export enum ChipsIconPosition {
    start = 'start',
    end = 'end',
}

export enum ChipsTypes {
    add = 'add',
    remove = 'remove',
    next = 'next',
    none = 'none',
    loading = 'loading',
}

export enum ChipsLayoutTypes {
    dashed = 'dashed',
    solid = 'solid',
}

export enum ChipsContentType {
    text = 'text',
    element = 'element',
}


export interface ChipsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'content'> {
    content?: ReactNode | ((hovering: boolean) => ReactNode),
    type?: ChipsTypes,
    layoutType?: ChipsLayoutTypes,
    contentType?: ChipsContentType,
    iconPosition?: ChipsIconPosition,
    onIconClick?: IconButtonProps['onClick'],
    disabled?: boolean,
    className?: string,
    buttonProps?: IconButtonProps,
    onClick?: React.MouseEventHandler<HTMLDivElement>,
}

const Chips: React.FC<ChipsProps> = ({
                                         content,
                                         type = ChipsTypes.next,
                                         layoutType = ChipsLayoutTypes.solid,
                                         contentType = ChipsContentType.text,
                                         onIconClick,
                                         iconPosition = ChipsIconPosition.start,
                                         disabled = false,
                                         className,
                                         buttonProps,
                                         onClick,
                                         ...props
                                     }) => {
    const classes = useStyles();
    const [hovering, setHovering] = useState(false);

    const iconComponent = useMemo(() => {
        const iconClassName = classnames({
            'mr-1': iconPosition === ChipsIconPosition.start,
            'ml-1': iconPosition === ChipsIconPosition.end,
        })
        switch (type) {
            case ChipsTypes.add:
                return (
                    <IconButton
                        onPressed={onIconClick}
                        // @ts-ignore
                        color={'secondary'}
                        disabled={disabled}
                        className={iconClassName}
                        size={'small'}
                        {...buttonProps}
                    >
                        <Add fontSize={'small'}/>
                    </IconButton>
                )
            case ChipsTypes.next:
                return (
                    <IconButton
                        onPressed={onIconClick}
                        // @ts-ignore
                        color={'secondary'}
                        disabled={disabled}
                        className={iconClassName}
                        size={'small'}
                        {...buttonProps}
                    >
                        <ArrowForwardIos fontSize={'small'}/>
                    </IconButton>
                );
            case ChipsTypes.remove:
                return (
                    <IconButton
                        onPressed={onIconClick}
                        // @ts-ignore
                        color={'secondary'}
                        disabled={disabled}
                        className={iconClassName}
                        size={'small'}
                        {...buttonProps}
                    >
                        <Close fontSize={'small'}/>
                    </IconButton>
                );
            case ChipsTypes.loading:
                return (
                    <IconButton
                        onPressed={onIconClick}
                        // @ts-ignore
                        color={'secondary'}
                        disabled={disabled}
                        className={iconClassName}
                        size={'small'}
                        {...buttonProps}
                    >
                        <CircularProgress size={20} color={'primary'}/>
                    </IconButton>
                );
            case ChipsTypes.none:
            default:
                return <></>
        }
    }, [buttonProps, disabled, iconPosition, onIconClick, type])


    const contentComponent = useMemo(() => {
        switch (contentType) {
            case ChipsContentType.element:
                if (typeof content === "function") {
                    return content(hovering);
                }
                return content;
            case ChipsContentType.text:
            default:
                return (
                    <p className={classnames('chips-default-content', {[layoutType]: layoutType, 'clickable': !!onClick},)}>
                        {typeof content === "function" ? content(hovering) : content}
                    </p>
                );
        }

    }, [content, contentType, hovering, layoutType, onClick])

    const contentSection = useMemo(() => {
        switch (iconPosition) {
            case ChipsIconPosition.start:
                return (
                    <>
                        {iconComponent}
                        {contentComponent}
                    </>
                )
            case ChipsIconPosition.end:
            default:
                return (
                    <>
                        {contentComponent}
                        {iconComponent}
                    </>
                )
        }
    }, [contentComponent, iconComponent, iconPosition])

    return (
        <div
            className={classnames(className, 'chips-component', {
                [classes.border]: layoutType === ChipsLayoutTypes.dashed,
                [layoutType]: layoutType,
            },)}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            onClick={onClick}
            {...props}
        >
            {contentSection}
        </div>
    );
}

export default Chips;
